// apps/web/src/pages/cadastros/acessos-portal-dialogs.tsx — Fase 17, Plano 13 (PORT-UI-22, PORT-UI-23; D-04, D-09, D-11).
//
// Diálogos da tela interna "Acessos do portal": convidar usuário, link gerado (entregue uma única vez) e as
// confirmações auditáveis (desativar, reativar, revogar link, gerar novo link). O valor do link vive somente no
// estado do diálogo que o exibe: quem abre o diálogo o recebe por prop e, ao fechar, o pai o descarta.
import { useEffect, useRef, useState, type FormEvent, type ReactElement, type Ref } from 'react';
import { Check, Copy, Loader2 } from 'lucide-react';
import type { AcessoPortalView, CriarAcessoPortalResposta, LinkPortalGerado, TipoLinkPortal } from '@pieltec/contracts';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  criarAcesso,
  ErroAcessoPortal,
  formatarDataHoraAcesso,
  mensagemErroAcesso,
  textosConfirmacao,
  validarConvite,
  type AcaoConfirmavel,
  type ErroAcessoTraduzido,
  type ErrosConvite,
} from '../../lib/portal-acessos';

export const MENSAGEM_FALHA_COPIA = 'Não foi possível copiar automaticamente. Selecione o link e copie manualmente.';
const DURACAO_COPIADO_MS = 3000;

const ID_NOME = 'acesso-portal-nome';
const ID_EMAIL = 'acesso-portal-email';
const ID_CLIENTE = 'acesso-portal-cliente';
const CLASSE_ALVO = 'min-h-11 lg:min-h-8';

// ─── Convidar usuário ───────────────────────────────────────────────────────────────────────────

export interface AcessoPortalFormularioProps {
  readonly clienteNome: string;
  readonly nome: string;
  readonly email: string;
  readonly erros?: ErrosConvite;
  /** Erro que não é de campo (permissão, cliente inexistente, rede). */
  readonly erroGeral?: string | null;
  readonly enviando?: boolean;
  readonly aoMudarNome?: (valor: string) => void;
  readonly aoMudarEmail?: (valor: string) => void;
  readonly aoEnviar?: () => void;
  readonly aoCancelar?: () => void;
}

/** Corpo do diálogo de convite (puro, testável sem o portal do Radix). */
export function AcessoPortalFormulario({
  clienteNome,
  nome,
  email,
  erros = {},
  erroGeral = null,
  enviando = false,
  aoMudarNome,
  aoMudarEmail,
  aoEnviar,
  aoCancelar,
}: AcessoPortalFormularioProps): ReactElement {
  const aoSubmeter = (evento: FormEvent<HTMLFormElement>): void => {
    evento.preventDefault();
    if (!enviando) aoEnviar?.();
  };
  return (
    <form noValidate onSubmit={aoSubmeter} data-testid="acessos-portal-convite-form" className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor={ID_CLIENTE}>Cliente</Label>
        <Input id={ID_CLIENTE} readOnly value={clienteNome} data-testid="acessos-portal-convite-cliente" />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={ID_NOME}>Nome</Label>
        <Input
          id={ID_NOME}
          value={nome}
          autoComplete="off"
          aria-required="true"
          aria-invalid={erros.nome ? true : undefined}
          aria-describedby={erros.nome ? `${ID_NOME}-erro` : undefined}
          onChange={(evento) => aoMudarNome?.(evento.target.value)}
          disabled={enviando}
        />
        {erros.nome ? <FormMessage id={`${ID_NOME}-erro`}>{erros.nome}</FormMessage> : null}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={ID_EMAIL}>E-mail</Label>
        <Input
          id={ID_EMAIL}
          type="email"
          value={email}
          autoComplete="off"
          aria-required="true"
          aria-invalid={erros.email ? true : undefined}
          aria-describedby={erros.email ? `${ID_EMAIL}-erro` : `${ID_EMAIL}-ajuda`}
          onChange={(evento) => aoMudarEmail?.(evento.target.value)}
          disabled={enviando}
          className="min-w-0"
        />
        <p id={`${ID_EMAIL}-ajuda`} className="text-[length:var(--font-label)] font-semibold text-muted-foreground">
          É o login do usuário e não pode repetir outro usuário do portal.
        </p>
        {erros.email ? <FormMessage id={`${ID_EMAIL}-erro`}>{erros.email}</FormMessage> : null}
      </div>
      {erroGeral ? <FormMessage data-testid="acessos-portal-convite-erro">{erroGeral}</FormMessage> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={aoCancelar} disabled={enviando} className={CLASSE_ALVO}>
          Cancelar
        </Button>
        <Button type="submit" disabled={enviando} data-testid="acessos-portal-convite-criar" className={CLASSE_ALVO}>
          {enviando ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
          {enviando ? 'Criando…' : 'Criar e gerar link'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export interface AcessoPortalDialogProps {
  readonly aberto: boolean;
  readonly cliente: { readonly id: string; readonly nome: string } | null;
  readonly aoFechar: () => void;
  /** O pai recebe o usuário criado e o link (entregue uma única vez). */
  readonly aoCriado: (resposta: CriarAcessoPortalResposta) => void;
}

export function AcessoPortalDialog({ aberto, cliente, aoFechar, aoCriado }: AcessoPortalDialogProps): ReactElement {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [erros, setErros] = useState<ErrosConvite>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setNome('');
    setEmail('');
    setErros({});
    setErroGeral(null);
    setEnviando(false);
  }, [aberto]);

  const enviar = async (): Promise<void> => {
    if (cliente === null) return;
    const invalidos = validarConvite(nome, email);
    setErros(invalidos);
    setErroGeral(null);
    if (invalidos.nome || invalidos.email) {
      document.getElementById(invalidos.nome ? ID_NOME : ID_EMAIL)?.focus();
      return;
    }
    setEnviando(true);
    try {
      const resposta = await criarAcesso({ clienteId: cliente.id, nome: nome.trim(), email: email.trim() });
      aoCriado(resposta);
    } catch (erro) {
      const traduzido = erro instanceof ErroAcessoPortal ? mensagemErroAcesso(erro.status, erro.mensagemServidor) : mensagemErroAcesso(null);
      if (traduzido.tipo === 'email') {
        setErros({ email: traduzido.mensagem });
        document.getElementById(ID_EMAIL)?.focus();
      } else {
        setErroGeral(traduzido.mensagem);
      }
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => (!proximo && !enviando ? aoFechar() : undefined)}>
      <DialogContent className="sm:max-w-lg" data-testid="acessos-portal-convite">
        <DialogHeader>
          <DialogTitle>Convidar usuário do portal</DialogTitle>
          <DialogDescription>
            O usuário só verá os pedidos de {cliente?.nome ?? 'o cliente'}. Depois de criar, você receberá um link de primeiro acesso para entregar a ele.
          </DialogDescription>
        </DialogHeader>
        <AcessoPortalFormulario
          clienteNome={cliente?.nome ?? ''}
          nome={nome}
          email={email}
          erros={erros}
          erroGeral={erroGeral}
          enviando={enviando}
          aoMudarNome={setNome}
          aoMudarEmail={setEmail}
          aoEnviar={() => void enviar()}
          aoCancelar={aoFechar}
        />
      </DialogContent>
    </Dialog>
  );
}

// ─── Link gerado (entregue uma única vez) ───────────────────────────────────────────────────────

export type EstadoCopia = 'inicial' | 'copiado' | 'falha';

export function tituloLinkGerado(tipo: TipoLinkPortal): string {
  return tipo === 'CONVITE' ? 'Link de primeiro acesso' : 'Link de redefinição de senha';
}

export interface LinkGeradoCorpoProps {
  readonly link: LinkPortalGerado;
  readonly nome: string;
  /** Redefinição para usuário ativo: a senha atual e a sessão aberta continuam valendo. */
  readonly senhaAtualContinua?: boolean;
  readonly estadoCopia?: EstadoCopia;
  readonly campoRef?: Ref<HTMLInputElement>;
  readonly aoCopiar?: () => void;
  readonly aoConcluir?: () => void;
}

/** Corpo do diálogo de link (puro): o link só aparece aqui, num `Input readonly` que rola sem cortar o valor. */
export function LinkGeradoCorpo({
  link,
  nome,
  senhaAtualContinua = false,
  estadoCopia = 'inicial',
  campoRef,
  aoCopiar,
  aoConcluir,
}: LinkGeradoCorpoProps): ReactElement {
  const copiado = estadoCopia === 'copiado';
  return (
    <div className="flex flex-col gap-4" data-testid="acessos-portal-link-corpo">
      <p id="acessos-portal-link-texto" className="text-muted-foreground">
        Copie o link e envie para {nome} por e-mail ou WhatsApp. O link só pode ser usado uma vez e expira em {formatarDataHoraAcesso(link.validoAte)}. Por
        segurança, ele não será mostrado de novo.
        {senhaAtualContinua ? ` A senha atual continua valendo até ${nome} definir a nova.` : ''}
      </p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          ref={campoRef}
          readOnly
          value={link.url}
          aria-label="Link de acesso"
          data-testid="acessos-portal-link-campo"
          onFocus={(evento) => evento.currentTarget.select()}
          className="min-w-0 flex-1"
        />
        <Button type="button" onClick={aoCopiar} data-testid="acessos-portal-link-copiar" className={`${CLASSE_ALVO} shrink-0`}>
          {copiado ? <Check aria-hidden="true" className="size-4" /> : <Copy aria-hidden="true" className="size-4" />}
          {copiado ? 'Link copiado' : 'Copiar link'}
        </Button>
      </div>
      <p
        role="status"
        data-testid="acessos-portal-link-status"
        className={estadoCopia === 'falha' ? 'text-destructive' : 'sr-only'}
      >
        {estadoCopia === 'falha' ? MENSAGEM_FALHA_COPIA : copiado ? 'Link copiado.' : ''}
      </p>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={aoConcluir} data-testid="acessos-portal-link-concluir" className={CLASSE_ALVO}>
          Já copiei o link
        </Button>
      </DialogFooter>
    </div>
  );
}

export interface LinkGeradoDialogProps {
  /** Link em memória; `null` fecha o diálogo e descarta o valor. */
  readonly link: LinkPortalGerado | null;
  readonly nome: string;
  readonly senhaAtualContinua?: boolean;
  readonly aoFechar: () => void;
}

export function LinkGeradoDialog({ link, nome, senhaAtualContinua = false, aoFechar }: LinkGeradoDialogProps): ReactElement {
  const [estadoCopia, setEstadoCopia] = useState<EstadoCopia>('inicial');
  const campoRef = useRef<HTMLInputElement>(null);
  const url = link?.url ?? null;

  useEffect(() => {
    setEstadoCopia('inicial');
  }, [url]);

  useEffect(() => {
    if (estadoCopia !== 'copiado') return;
    const temporizador = window.setTimeout(() => setEstadoCopia('inicial'), DURACAO_COPIADO_MS);
    return () => window.clearTimeout(temporizador);
  }, [estadoCopia]);

  const copiar = async (): Promise<void> => {
    if (link === null) return;
    try {
      if (typeof navigator === 'undefined' || navigator.clipboard === undefined) throw new Error('clipboard indisponível');
      await navigator.clipboard.writeText(link.url);
      setEstadoCopia('copiado');
    } catch {
      setEstadoCopia('falha');
      campoRef.current?.focus();
      campoRef.current?.select();
    }
  };

  return (
    <Dialog open={link !== null} onOpenChange={(proximo) => (!proximo ? aoFechar() : undefined)}>
      <DialogContent className="sm:max-w-lg" aria-describedby="acessos-portal-link-texto" data-testid="acessos-portal-link-dialogo">
        <DialogHeader>
          <DialogTitle>{link === null ? 'Link de acesso' : tituloLinkGerado(link.tipo)}</DialogTitle>
        </DialogHeader>
        {link === null ? null : (
          <LinkGeradoCorpo
            link={link}
            nome={nome}
            senhaAtualContinua={senhaAtualContinua}
            estadoCopia={estadoCopia}
            campoRef={campoRef}
            aoCopiar={() => void copiar()}
            aoConcluir={aoFechar}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Confirmações ───────────────────────────────────────────────────────────────────────────────

/** Falha da ação dentro da confirmação: o diálogo fica aberto e a linha não muda; conflito oferece Recarregar. */
export function ErroAcaoConfirmacao({ erro, aoRecarregar }: { readonly erro: ErroAcessoTraduzido; readonly aoRecarregar?: () => void }): ReactElement {
  return (
    <div className="flex flex-col items-start gap-2">
      <FormMessage data-testid="acessos-portal-erro-confirmacao">{erro.mensagem}</FormMessage>
      {erro.recarregar ? (
        <Button type="button" variant="outline" onClick={aoRecarregar} className={CLASSE_ALVO}>
          Recarregar
        </Button>
      ) : null}
    </div>
  );
}

export interface AcessoConfirmarDialogProps {
  readonly acao: AcaoConfirmavel | null;
  readonly acesso: AcessoPortalView | null;
  readonly clienteNome: string;
  readonly enviando: boolean;
  readonly erro: ErroAcessoTraduzido | null;
  readonly aoConfirmar: () => void;
  readonly aoCancelar: () => void;
  readonly aoRecarregar: () => void;
}

/** `AlertDialog`: o foco inicial vai para o botão de dispensa (Radix), nunca para o destrutivo; `Esc` dispensa. */
export function AcessoConfirmarDialog({
  acao,
  acesso,
  clienteNome,
  enviando,
  erro,
  aoConfirmar,
  aoCancelar,
  aoRecarregar,
}: AcessoConfirmarDialogProps): ReactElement {
  const textos = acao !== null && acesso !== null ? textosConfirmacao(acao, acesso, clienteNome) : null;
  return (
    <AlertDialog open={textos !== null} onOpenChange={(proximo) => (!proximo && !enviando ? aoCancelar() : undefined)}>
      <AlertDialogContent data-testid="acessos-portal-confirmar">
        {textos === null ? null : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{textos.titulo}</AlertDialogTitle>
              <AlertDialogDescription>{textos.corpo}</AlertDialogDescription>
            </AlertDialogHeader>
            {erro ? <ErroAcaoConfirmacao erro={erro} aoRecarregar={aoRecarregar} /> : null}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={enviando} className={CLASSE_ALVO}>
                {textos.cancelar}
              </AlertDialogCancel>
              <Button
                type="button"
                variant={textos.destrutiva ? 'destructive' : 'default'}
                onClick={aoConfirmar}
                disabled={enviando}
                className={CLASSE_ALVO}
              >
                {enviando ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
                {enviando ? textos.enviando : textos.confirmar}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
